import type { AppDispatch } from '@/app/store';
import type { AnalyticsQuery } from '@/shared/api';
import { csv, type CsvColumn, type CsvValue } from '@/shared/lib';
import { analyticsApi } from '../api/analyticsApi';
import { readValue } from './records';
import type { AnalyticsColumn, AnalyticsObject } from './schema';

/**
 * Rows an export fetches at most: pages of the backend's largest size, each a query of its own,
 * which on a large unindexed table can take seconds.
 */
export const RECORD_EXPORT_LIMIT = 1000;
/** AnalyticsQueryBuilder.MaxPageSize. */
const EXPORT_PAGE = 100;

/** A stored value as a CSV cell: dates as days, other values as the API sends them. */
function exportValue(column: AnalyticsColumn, value: unknown): CsvValue {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') return column.kind === 'date' ? csv.date(value) : value;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  return JSON.stringify(value);
}

/** The shown columns as export columns, labelled as the table labels them. */
export function recordExportColumns(columns: readonly AnalyticsColumn[]): CsvColumn<object>[] {
  return columns.map((column) => ({
    label: column.label,
    value: (row) => exportValue(column, readValue(row, column)),
  }));
}

/** The rows the query matches, in its order, page by page up to `RECORD_EXPORT_LIMIT`. */
export async function loadRecords(
  dispatch: AppDispatch,
  object: AnalyticsObject,
  query: AnalyticsQuery,
): Promise<object[]> {
  const rows: object[] = [];
  for (let pageNumber = 1; rows.length < RECORD_EXPORT_LIMIT; pageNumber += 1) {
    const page = await dispatch(
      analyticsApi.endpoints.getAnalyticsPage.initiate(
        { path: object.path, query: { ...query, pageNumber, pageSize: EXPORT_PAGE } },
        { subscribe: false },
      ),
    ).unwrap();
    rows.push(...page.items);
    if (page.items.length < EXPORT_PAGE || pageNumber >= page.totalPages) break;
  }
  return rows.slice(0, RECORD_EXPORT_LIMIT);
}

/** Tooltip of the button: how many records the file will hold. */
export function recordExportTitle(total: number | undefined, estimate = false): string {
  if (total === undefined) return 'Download the records as CSV';
  const count = `${estimate ? '≈ ' : ''}${total.toLocaleString('en-US')}`;
  return total > RECORD_EXPORT_LIMIT
    ? `Download the first ${RECORD_EXPORT_LIMIT.toLocaleString('en-US')} of ${count} records as CSV`
    : `Download all ${count} records as CSV`;
}
